import {
  Button,
  Card,
  Grid,
  message,
  Row,
  Space,
  Tabs,
  TabsProps,
  Tooltip,
  Upload,
} from "antd";
import React, { useContext, useEffect, useRef, useState } from "react";
import { ColumnComponent, TagSelector } from "../../components/ui";
import Table from "antd/es/table";
import DataContext from "../../context/DataContext";
import { useParams } from "react-router-dom";
import AuthContext from "../../context/AuthContext";
import {
  DownloadOutlined,
  EditOutlined,
  FolderAddOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { MultipleSelect, SearchInput } from "../../components/functional";
import {
  CustomerProps,
  ProjectProps,
  TagProps,
} from "../../interfaces/interfaces";
import { useDemoModal } from "../../hooks";
import { exportProjectList } from "../../utils/func/ExportFile";
import { CustomerDrawer } from "../../components/customer";
import {
  DrawerCreateOrUpdateProject,
  ProjectList,
} from "../../components/project";
import { PROJECT_INITIAL_STATE } from "../../utils/data";
import ProjectImport from "../../components/project/ProjectImport";
import { UploadProps } from "antd/lib";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

export const ProjectListPage = () => {
  const { useBreakpoint } = Grid;
  const { xs } = useBreakpoint();
  const { idClient } = useParams();
  const { clientInformation, isDemoEnv, getClientInformation } =
    useContext(AuthContext);
  const { DemoModal, openModalDemo } = useDemoModal();
  const [activeTab, setActiveTab] = useState("1");
  const {
    getData,
    projectListPaginated,
    projectModal,
    usersList,
    projectTemplateList,
    handleChangeProjectSelected,
    openItemModal,
    closeItemModal,
    handleTour,
    handleCreateData,
    handleEditData,
    handleDeleteData,
    getCustomersListByClient,
    handleChangeProjectModalStatus,
    uploadFile,
  } = useContext(DataContext);
  const [modalImportProject, setModalImportProject] = useState(false);
  const [selectedRowsToEdit, setSelectedRowToEdit] = useState<React.Key[]>([]);
  const [tagsSelected, setTagsSelected] = useState<TagProps[]>([]);
  const [userSelected, setUserSelected] = useState<string[]>([]);
  const [templateSelected, setTemplateSelected] = useState<string[]>([]);
  const [customerDrawerStatus, setCustomerDrawerStatus] = useState<{
    open: boolean;
    idCustomer: string | null;
  }>({ open: false, idCustomer: "" });
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedData, setUploadedData] = useState<ProjectProps[]>([]);

  const ref1 = useRef(null);

  useEffect(() => {
    if (idClient) {
      getData("projects-paginated", idClient, undefined, {
        active: true,
      });
      getData("project-templates", idClient, undefined, {
        active: true,
      });
      getData("projects", idClient, undefined, {
        active: true,
      });
      getData("users", idClient, undefined, { active: true });
      getCustomersListByClient({ idClient });
      getClientInformation(idClient, () => {});
    }
  }, [idClient]);

  useEffect(() => {
    if (isDemoEnv) {
      let tourOpened = localStorage.getItem("tour-summary");
      if (!tourOpened) {
        handleTour(true);
        localStorage.setItem("tour-customer", "true");
      }
    }
  }, []);

  const onChangeSelectedRowsToEdit = (newSelectedRowToEdit: React.Key[]) => {
    setSelectedRowToEdit(newSelectedRowToEdit);
  };

  const rowSelection = {
    selectedRowKeys: selectedRowsToEdit,
    onChange: onChangeSelectedRowsToEdit,
    selections: [Table.SELECTION_ALL, Table.SELECTION_NONE],
  };

  const hasSelected = selectedRowsToEdit.length > 0;

  const createOrUpdateProject = (
    values: ProjectProps,
    idClient: string,
    idProject?: string,
  ) => {
    let filters = {};
    if (tagsSelected.length > 0) {
      filters = {
        ...filters,
        tags: {
          $in: tagsSelected.map((tag) => tag._id),
        },
      };
    }
    if (userSelected.length > 0) {
      filters = {
        ...filters,
        assignedTo: {
          $in: userSelected,
        },
      };
    }
    if (templateSelected.length > 0) {
      filters = {
        ...filters,
        projectListPaginated: {
          $in: templateSelected,
        },
      };
    }
    if (projectModal?.create) {
      handleCreateData({
        endpoint: "projects",
        idClient,
        data: values,
        next: () =>
          getData("projects-paginated", idClient, undefined, {
            active: activeTab === "1",
            ...filters,
          }),
      });
    } else if (projectModal?.update && idProject) {
      handleEditData({
        endpoint: `project/${idClient}/${idProject}`,
        newData: values,
        next: () =>
          getData("projects-paginated", idClient, undefined, {
            active: activeTab === "1",
            ...filters,
          }),
      });
    } else if (projectModal?.update && projectModal?.selected) {
      handleEditData({
        endpoint: `projects/${idClient}`,
        newData: {
          ids: selectedRowsToEdit,
          updates: values,
        },
        next: () =>
          getData("projects-paginated", idClient, undefined, {
            active: activeTab === "1",
            ...filters,
          }),
      });
    }
  };

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: "Active",
      children: (
        <ProjectList
          onSelectOne={(project) => {
            handleChangeProjectSelected(project._id);
            openItemModal("project", false, project);
          }}
          rowSelection={rowSelection}
          onChangePage={(current, pageSize) => {
            idClient &&
              getData("projects-paginated", idClient, undefined, {
                active: true,
                page: current,
                perPage: pageSize,
              });
          }}
          active={true}
        />
      ),
    },
    {
      key: "2",
      label: "Archived",
      children: (
        <ProjectList
          onSelectOne={(project) => openItemModal("project", false, project)}
          rowSelection={rowSelection}
          onChangePage={(current, pageSize) => {
            idClient &&
              getData("projects-paginated", idClient, undefined, {
                active: false,
                page: current,
                perPage: pageSize,
              });
          }}
          active={false}
          onDeleteProject={(project: ProjectProps) => {
            handleDeleteData({
              endpoint: `project/${idClient}/${project._id}`,
              next: () => {
                idClient &&
                  getData("projects-paginated", idClient, undefined, {
                    active: false,
                  });
              },
            });
          }}
        />
      ),
    },
  ];

  const onChange = (key: string) => {
    setActiveTab(key);
    if (key === "1") {
      idClient &&
        getData("projects-paginated", idClient, undefined, {
          active: true,
        });
    } else if (key === "2") {
      idClient &&
        getData("projects-paginated", idClient, undefined, {
          active: false,
        });
    }
  };

  const handleFilterByTags = (selectedTags: TagProps[]) => {
    let filters = {};
    if (userSelected.length > 0) {
      filters = {
        ...filters,
        assignedTo: {
          $in: userSelected,
        },
      };
    }
    if (templateSelected.length > 0) {
      filters = {
        ...filters,
        projectTemplate: {
          $in: templateSelected,
        },
      };
    }
    if (selectedTags.length > 0) {
      setTagsSelected(selectedTags);
      filters = {
        ...filters,
        tags: {
          $in: selectedTags.map((tag) => tag._id),
        },
      };
      if (idClient) {
        getData("projects-paginated", idClient, undefined, {
          active: activeTab === "1",
          ...filters,
        });
      }
    } else {
      setTagsSelected([]);
      idClient &&
        getData("projects-paginated", idClient, undefined, {
          active: activeTab === "1",
          ...filters,
        });
    }
  };

  const handleFilterByAssigned = (idUsers: string[]) => {
    let filters = {};
    if (tagsSelected.length > 0) {
      filters = {
        ...filters,
        tags: {
          $in: tagsSelected.map((tag) => tag._id),
        },
      };
    }
    if (idUsers.length > 0) {
      filters = {
        ...filters,
        assignedTo: {
          $in: idUsers,
        },
      };
      if (idClient) {
        getData("projects-paginated", idClient, undefined, {
          active: activeTab === "1",
          ...filters,
        });
      }
    } else {
      idClient &&
        getData("projects-paginated", idClient, undefined, {
          active: activeTab === "1",
          ...filters,
        });
    }
  };
  const handleFilterByTemplate = (idTemplates: string[]) => {
    let filters = {};
    if (tagsSelected.length > 0) {
      filters = {
        ...filters,
        tags: {
          $in: tagsSelected.map((tag) => tag._id),
        },
      };
    }
    if (idTemplates.length > 0) {
      filters = {
        ...filters,
        projectTemplate: {
          $in: idTemplates,
        },
      };
      if (idClient) {
        getData("projects-paginated", idClient, undefined, {
          active: activeTab === "1",
          ...filters,
        });
      }
    } else {
      idClient &&
        getData("projects-paginated", idClient, undefined, {
          active: activeTab === "1",
          ...filters,
        });
    }
  };

  function isExcelDate(value: any): boolean {
    return typeof value === "number" && value > 25569 && value < 60000;
  }

  function parseExcelDate(serial: number): Date {
    // Excel serial date to UTC, then add 5 hours to match incoming date
    const utcDate = dayjs.utc((serial - 25569) * 86400 * 1000).add(5, "hour");
    return utcDate.toDate();
  }

  const props: UploadProps = {
    name: "file",
    accept: ".xlsx",
    showUploadList: false,
    customRequest: async ({ file, onSuccess: onUploadSuccess, onError }) => {
      setLoading(true);
      try {
        const reader = new FileReader();
        reader.onload = async (e) => {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array", cellDates: false });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const rawData = XLSX.utils.sheet_to_json(sheet, {
            defval: null,
            raw: true,
          });
          const parsed = rawData.map((row: any) => {
            const parsedRow: any = {};
            for (const [key, value] of Object.entries(row)) {
              if (isExcelDate(value)) {
                parsedRow[key] = parseExcelDate(value as any);
              } else {
                parsedRow[key] =
                  typeof value === "string" ? value.trim() : value;
              }
            }
            return parsedRow;
          });
          setPreviewData(parsed as any[]);
          setUploadedFile(file as File);
          setModalImportProject(true);

          message.success("Preview loaded. Ready to upload.");
          onUploadSuccess && onUploadSuccess("ok");
        };
        reader.readAsArrayBuffer(file as File);
      } catch (err) {
        console.error(err);
        message.error("Failed to load preview");
        // onError && onError(err);
      } finally {
        setLoading(false);
      }
    },
  };

  const handleConfirmUpload = async (columnMapping: any, next: () => void) => {
    if (!uploadedFile) {
      message.error("No file uploaded.");
      return;
    }

    let formData = new FormData();
    formData.append("file", uploadedFile);
    formData.append("mapping", JSON.stringify(columnMapping));
    try {
      idClient &&
        uploadFile({
          endpoint: `projects-upload/${idClient}`,
          formData,
          next: (response: { message: string; data: ProjectProps[] }) => {
            if (response.data?.length === 0) {
              message.error("No new data found in the file");
              return;
            }
            setUploadedData(response.data);
            getClientInformation(idClient || "", () => {});
            message.success("Projects imported successfully");
            next();
          },
        });
    } catch (error) {
      console.error(error);
      message.error("Upload failed");
    }
  };

  return (
    <>
      <Row gutter={[24, 0]}>
        <ColumnComponent>
          <div ref={ref1}>
            <Card
              className="summary-client__card"
              styles={{ body: { display: "none" } }}
              actions={[
                <Space direction="vertical" size={0} style={{ width: "100%" }}>
                  <label>Users</label>
                  <Tooltip title="Filter By Assigned">
                    <MultipleSelect
                      placeholder="Filter By Assigned"
                      className="summary-client__card__actions__select"
                      options={usersList.map((user) => ({
                        label: user.name,
                        value: user._id,
                      }))}
                      onSelect={(values) => {
                        if (values) {
                          setUserSelected(values);
                        } else {
                          setUserSelected([]);
                        }
                        handleFilterByAssigned(values as string[]);
                      }}
                    />
                  </Tooltip>
                </Space>,
                <Space direction="vertical" size={0} style={{ width: "100%" }}>
                  <label>Templates</label>
                  <Tooltip title="Filter By Template">
                    <MultipleSelect
                      placeholder="Filter By Template"
                      className="summary-client__card__actions__select"
                      options={projectTemplateList.map((template) => ({
                        label: template.name,
                        value: template._id,
                      }))}
                      onSelect={(values) => {
                        if (values) {
                          setTemplateSelected(values);
                        } else {
                          setTemplateSelected([]);
                        }
                        handleFilterByTemplate(values as string[]);
                      }}
                    />
                  </Tooltip>
                </Space>,
                <Space direction="vertical" size={0} style={{ width: "100%" }}>
                  <label>Tags</label>
                  <TagSelector
                    className="summary-client__card__actions__select"
                    size="middle"
                    selectedTags={tagsSelected}
                    setSelectedTags={(selected) => handleFilterByTags(selected)}
                    filtering={true}
                  />
                </Space>,
                <Space direction="vertical" size={0} style={{ width: "100%" }}>
                  <label>Search</label>
                  <SearchInput
                    onSearch={(value: string) => {
                      if (value) {
                        idClient &&
                          getData("projects-paginated", idClient, value, {
                            active: activeTab === "1",
                          });
                      } else {
                        idClient &&
                          getData("projects-paginated", idClient, undefined, {
                            active: activeTab === "1",
                          });
                      }
                    }}
                  />
                </Space>,
                <Space direction="vertical" size={0} style={{ width: "100%" }}>
                  <label>Actions</label>
                  <Space direction="horizontal" size={0}>
                    <Tooltip title="Create new Project" placement="bottom">
                      <Button
                        type="link"
                        icon={<FolderAddOutlined />}
                        size="large"
                        onClick={() => openItemModal("project", true)}
                      />
                    </Tooltip>
                    <Tooltip title="Import Projects" placement="bottom">
                      <Upload {...props}>
                        <Button
                          type="link"
                          disabled={xs}
                          icon={<UploadOutlined />}
                          loading={loading}
                        ></Button>
                      </Upload>
                    </Tooltip>
                    <Tooltip
                      title="Edit Selected Project(s)"
                      placement="bottom"
                    >
                      <Button
                        type="link"
                        icon={<EditOutlined />}
                        disabled={!hasSelected}
                        size="large"
                        onClick={() =>
                          openItemModal(
                            "project",
                            false,
                            {
                              ...PROJECT_INITIAL_STATE,
                              active: activeTab === "1",
                            },
                            "",
                            selectedRowsToEdit as string[],
                          )
                        }
                      />
                    </Tooltip>
                    <Tooltip
                      title={
                        selectedRowsToEdit.length > 0
                          ? "Export Selected Data"
                          : "Export Data"
                      }
                      placement="bottom"
                    >
                      <Button
                        type="link"
                        icon={<DownloadOutlined />}
                        size="large"
                        onClick={() => {
                          exportProjectList(
                            projectListPaginated.data,
                            selectedRowsToEdit as string[],
                          );
                        }}
                      />
                    </Tooltip>
                  </Space>
                </Space>,
              ]}
            ></Card>
          </div>
        </ColumnComponent>
        <ColumnComponent>
          <Tabs
            defaultActiveKey="1"
            items={items}
            style={{ padding: "0 12px" }}
            onChange={onChange}
            activeKey={activeTab}
          />
        </ColumnComponent>
      </Row>

      {idClient && (
        <CustomerDrawer
          open={customerDrawerStatus.open}
          idClient={idClient}
          idCustomer={customerDrawerStatus.idCustomer}
          onSelectOne={(record: CustomerProps) => {
            setCustomerDrawerStatus({ open: true, idCustomer: record._id });
          }}
          onClose={() =>
            setCustomerDrawerStatus({ open: false, idCustomer: "" })
          }
        />
      )}
      <DrawerCreateOrUpdateProject
        idClient={idClient || ""}
        modalStatus={projectModal}
        onSubmit={(values) => {
          if (isDemoEnv) {
            openModalDemo();
          } else {
            if (clientInformation && idClient) {
              createOrUpdateProject(values, idClient, values._id);
              closeItemModal("project");
            }
          }
        }}
        handleChangeModalStatus={(payload) =>
          handleChangeProjectModalStatus({
            ...projectModal,
            payload: payload || projectModal?.payload,
            create: false,
            update: true,
          } as any)
        }
        handleCreateSource={(source) => {
          handleEditData({
            endpoint: `client/${idClient}`,
            newData: {
              newSource: source,
            },
            next: () => {
              idClient && getClientInformation(idClient, () => {});
            },
            showAlert: false,
          });
        }}
        handleCreateLocation={(locationName) => {
          handleEditData({
            endpoint: `client/${idClient}`,
            newData: {
              newLocation: locationName,
            },
            next: () => {
              idClient && getClientInformation(idClient, () => {});
            },
          });
        }}
        handleCreateNewProjectStatus={(projectStatusName) => {
          handleEditData({
            endpoint: `client/${idClient}`,
            newData: {
              newProjectStatus: projectStatusName,
            },
            next: () => {
              idClient && getClientInformation(idClient, () => {});
            },
            showAlert: false,
          });
        }}
        onCancel={() => {
          closeItemModal("project");
          handleChangeProjectSelected("");
        }}
      />
      <ProjectImport
        idClient={idClient}
        uploadedData={uploadedData}
        open={modalImportProject}
        previewData={previewData}
        onClose={() => {
          setModalImportProject(false);
          setPreviewData([]);
          setUploadedData([]);
        }}
        handleConfirmUpload={(columnMapping, next) => {
          handleConfirmUpload(columnMapping, next);
        }}
        handleConfirmCreate={(values) => {
          if (isDemoEnv) {
            openModalDemo();
          } else {
            if (clientInformation && idClient) {
              handleCreateData({
                endpoint: "projects-multiple",
                idClient,
                data: values,
                next: () => {
                  getData("templates", idClient, undefined, {
                    active: true,
                  });
                  getData("projects-paginated", idClient, undefined, {
                    active: activeTab === "1",
                  });
                },
              });
            }
          }
        }}
      />
      <DemoModal />
    </>
  );
};
